import type { TFunction } from 'i18next';
import { z } from 'zod';
import type { GetAirlineResponse } from '@/api/generated/schemas';
import {
  AirlineCheckInType,
  AirlineStatus,
  AirlineTicketTypes,
  DeadlineUnit,
  NameChangeFeeMode,
} from '@/api/generated/schemas';

/**
 * Strategie nákupu — na drátě tři nezávislé `bool` sloupce, ve formuláři jeden vícehodnotový výběr.
 * Vlastní UI hodnoty, protože backend pro ně žádný enum nemá (viz spec §4.4).
 */
export const AIRLINE_PURCHASE_STRATEGIES = ['OneWay', 'RoundTrip', 'Multicity'] as const;

export type AirlinePurchaseStrategy = (typeof AIRLINE_PURCHASE_STRATEGIES)[number];

/**
 * Povolení změn jmen — na drátě existence celého bloku podmínek (`nameChangePolicy`), ve formuláři
 * dvouhodnotový přepínač. Samostatný příznak povolení backend nedrží (viz spec §6.2).
 */
export const AIRLINE_NAME_CHANGE_MODES = ['Allowed', 'NotAllowed'] as const;

export type AirlineNameChangeMode = (typeof AIRLINE_NAME_CHANGE_MODES)[number];

/**
 * Pole, která žijí na záložce Extra services — zbytek schématu patří záložce General. Detail
 * podle nich po neúspěšném uložení pozná, že chyba leží na neaktivní záložce, a přepne se na ni.
 */
export const AIRLINE_EXTRA_SERVICES_FIELDS = [
  'ticketTypes',
  'purchaseStrategies',
  'checkInType',
  'checkInWindowFromHours',
  'checkInWindowToHours',
  'nameChanges',
  'nameChangeDeadlineValue',
  'nameChangeDeadlineUnit',
  'nameChangeFeeMode',
  'nameChangeOnlineFee',
  'nameChangeAirportFee',
  'nameChangeNamelistValue',
  'nameChangeNamelistUnit',
  'nameChangeNote',
] as const satisfies ReadonlyArray<keyof AirlineFormValues>;

/** UI ref organizace (stabilní `Party.ID`) — tvar `OrganizationRef` z `@/modules/general`. */
const organizationRefSchema = z.object({
  partyId: z.number(),
  name: z.string(),
});

/** UI ref měny — tvar `CurrencyRef` z `@/modules/codebooks`. */
const currencyRefSchema = z.object({
  id: z.number(),
  code: z.string(),
  name: z.string(),
});

/** IATA / ICAO — právě N písmen nebo číslic; prázdná hodnota je povolená (kód není povinný). */
function optionalCode(length: number, message: string) {
  return z
    .string()
    .refine(
      (raw) => raw.trim() === '' || new RegExp(`^[A-Za-z0-9]{${length}}$`).test(raw.trim()),
      message
    );
}

/** Tři `bool` pole API → jeden vícehodnotový výběr ve formuláři (pořadí drží `AIRLINE_PURCHASE_STRATEGIES`). */
function toPurchaseStrategies(flags: {
  allowsOneWay: boolean;
  allowsRoundTrip: boolean;
  allowsMulticity: boolean;
}): AirlinePurchaseStrategy[] {
  const selected: AirlinePurchaseStrategy[] = [];
  if (flags.allowsOneWay) selected.push('OneWay');
  if (flags.allowsRoundTrip) selected.push('RoundTrip');
  if (flags.allowsMulticity) selected.push('Multicity');
  return selected;
}

/**
 * Celé číslo z textového pole (meze okna check-inu, lhůty změn jmen); `null` = prázdné nebo necelé
 * číslo. Sdílené se `toAirlineRequest`, aby schéma a tělo requestu četly stejný text stejně.
 */
export function airlineInteger(raw: string): number | null {
  const value = Number(raw.trim());
  return raw.trim() !== '' && Number.isInteger(value) ? value : null;
}

/** Peněžní částka z textového pole; `null` = prázdné nebo nečíselné. */
export function airlineAmount(raw: string): number | null {
  const value = Number(raw.trim());
  return raw.trim() !== '' && Number.isFinite(value) ? value : null;
}

/**
 * Factory Zod schématu detailu letecké společnosti — UI zrcadlo BE validace
 * (`AirlineWriteValidator`); hlášky z `t` až za běhu.
 */
export function buildAirlineFormSchema(t: TFunction) {
  return z
    .object({
      organization: organizationRefSchema.nullable(),
      tradeName: z
        .string()
        .trim()
        .min(1, t('airline.validation.tradeNameRequired'))
        .max(50, t('airline.validation.tradeNameMaxLength')),
      iata: optionalCode(2, t('airline.validation.iataFormat')),
      icao: optionalCode(3, t('airline.validation.icaoFormat')),
      callsign: z.string().max(50, t('airline.validation.callsignMaxLength')),
      web: z
        .string()
        .max(255, t('airline.validation.webMaxLength'))
        .refine((raw) => raw.trim() === '' || /^https?:\/\/\S+$/i.test(raw.trim()), {
          message: t('airline.validation.webAbsoluteUrl'),
        }),
      currency: currencyRefSchema.nullable(),
      status: z.enum(AirlineStatus),
      note: z.string(),
      // Bez implicitního defaultu — nevybraná hodnota je `null`, ať nejde uložit domněnku jako fakt.
      ticketTypes: z.enum(AirlineTicketTypes).nullable(),
      purchaseStrategies: z.array(z.enum(AIRLINE_PURCHASE_STRATEGIES)),
      checkInType: z.enum(AirlineCheckInType).nullable(),
      // Meze okna jsou textová pole (jako ostatní čísla ve formulářích) — validují se v superRefine.
      checkInWindowFromHours: z.string(),
      checkInWindowToHours: z.string(),
      // Existence podmínek změn jmen = „ano"; `NotAllowed` celý blok i s hodnotami zahodí.
      nameChanges: z.enum(AIRLINE_NAME_CHANGE_MODES),
      nameChangeDeadlineValue: z.string(),
      // Jednotka je vždycky vybraná, takže lhůta je buď úplná dvojice, nebo (bez čísla) prázdná.
      nameChangeDeadlineUnit: z.enum(DeadlineUnit),
      nameChangeFeeMode: z.enum(NameChangeFeeMode).nullable(),
      nameChangeOnlineFee: z.string(),
      nameChangeAirportFee: z.string(),
      nameChangeNamelistValue: z.string(),
      nameChangeNamelistUnit: z.enum(DeadlineUnit),
      nameChangeNote: z.string(),
    })
    .superRefine((values, ctx) => {
      // Organizace je povinná, ale hodnota pole zůstává nullable — jinak by se typ formuláře
      // rozešel s tím, co selektor umí vrátit (prázdný výběr).
      if (values.organization === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['organization'],
          message: t('airline.validation.organizationRequired'),
        });
      }

      if (values.ticketTypes === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['ticketTypes'],
          message: t('airline.validation.ticketTypesRequired'),
        });
      }

      if (values.purchaseStrategies.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['purchaseStrategies'],
          message: t('airline.validation.purchaseStrategyRequired'),
        });
      }

      if (values.checkInType === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['checkInType'],
          message: t('airline.validation.checkInTypeRequired'),
        });
      }

      // Podmínky změn jmen se kontrolují, jen když je dopravce umožňuje — jinak se celý blok
      // i s vyplněnými hodnotami zahazuje, takže není co validovat.
      if (values.nameChanges === 'Allowed') {
        if (values.nameChangeFeeMode === null) {
          ctx.addIssue({
            code: 'custom',
            path: ['nameChangeFeeMode'],
            message: t('airline.validation.nameChangeFeeModeRequired'),
          });
        }

        for (const path of ['nameChangeDeadlineValue', 'nameChangeNamelistValue'] as const) {
          const raw = values[path];
          if (raw.trim() !== '' && (airlineInteger(raw) ?? -1) < 0) {
            ctx.addIssue({
              code: 'custom',
              path: [path],
              message: t('airline.validation.nameChangeDeadlineNonNegative'),
            });
          }
        }

        for (const path of ['nameChangeOnlineFee', 'nameChangeAirportFee'] as const) {
          const raw = values[path];
          if (raw.trim() === '') {
            continue;
          }

          if ((airlineAmount(raw) ?? -1) < 0) {
            ctx.addIssue({
              code: 'custom',
              path: [path],
              message: t('airline.validation.nameChangeFeeNonNegative'),
            });
          } else if (values.currency === null) {
            // Poplatky vlastní měnu nemají — platí měna dopravce a ukládá se stejným requestem,
            // takže se srovnává s hodnotou formuláře (na rozdíl od cen pravidel zavazadel).
            ctx.addIssue({
              code: 'custom',
              path: [path],
              message: t('airline.validation.nameChangeFeeCurrencyRequired'),
            });
          }
        }
      }

      // Okno se zadává jen u online odbavení; u letištního ho formulář ani neukazuje a server ho
      // normalizuje na null, takže se tu nekontroluje.
      if (values.checkInType !== AirlineCheckInType.Online) {
        return;
      }

      const from = airlineInteger(values.checkInWindowFromHours);
      const to = airlineInteger(values.checkInWindowToHours);

      for (const [path, value] of [
        ['checkInWindowFromHours', from],
        ['checkInWindowToHours', to],
      ] as const) {
        if (value === null || value < 0) {
          ctx.addIssue({
            code: 'custom',
            path: [path],
            message: t('airline.validation.checkInWindowRequired'),
          });
        }
      }

      // Obě meze jsou „hodin před odletem", takže začátek okna je to větší číslo.
      if (from !== null && to !== null && from >= 0 && to >= 0 && from <= to) {
        ctx.addIssue({
          code: 'custom',
          path: ['checkInWindowFromHours'],
          message: t('airline.validation.checkInWindowOrder'),
        });
      }
    });
}

export type AirlineFormValues = z.infer<ReturnType<typeof buildAirlineFormSchema>>;

export function emptyAirlineFormValues(): AirlineFormValues {
  return {
    organization: null,
    tradeName: '',
    iata: '',
    icao: '',
    callsign: '',
    web: '',
    currency: null,
    // Jediný automatický business default — ostatní volby si vybírá uživatel.
    status: AirlineStatus.Active,
    note: '',
    ticketTypes: null,
    purchaseStrategies: [],
    checkInType: null,
    checkInWindowFromHours: '',
    checkInWindowToHours: '',
    // Výchozí stav nového dopravce — změny jmen nepovolené (spec §6.2).
    nameChanges: 'NotAllowed',
    nameChangeDeadlineValue: '',
    nameChangeDeadlineUnit: DeadlineUnit.Hours,
    nameChangeFeeMode: null,
    nameChangeOnlineFee: '',
    nameChangeAirportFee: '',
    nameChangeNamelistValue: '',
    nameChangeNamelistUnit: DeadlineUnit.Hours,
    nameChangeNote: '',
  };
}

/**
 * Mapuje detail z API na hodnoty formuláře (režim editace). Nevalidní historický záznam se načte
 * bez omezení — validace se uplatní až při uložení, aby ho šlo v UI opravit.
 */
export function fromGetAirlineResponse(response: GetAirlineResponse): AirlineFormValues {
  // Chybějící blok podmínek znamená „dopravce změny jmen neumožňuje" — jiný příznak neexistuje.
  const policy = response.nameChangePolicy;

  return {
    organization:
      response.organizationPartyId === null
        ? null
        : { partyId: response.organizationPartyId, name: response.organizationName ?? '' },
    tradeName: response.tradeName,
    iata: response.iata ?? '',
    icao: response.icao ?? '',
    callsign: response.callsign ?? '',
    web: response.web ?? '',
    currency:
      response.currencyId === null
        ? null
        : { id: response.currencyId, code: response.currencyCode ?? '', name: '' },
    status: response.status,
    note: response.note ?? '',
    ticketTypes: response.ticketTypes,
    purchaseStrategies: toPurchaseStrategies(response),
    checkInType: response.checkInType,
    checkInWindowFromHours: response.checkInWindowFromHours?.toString() ?? '',
    checkInWindowToHours: response.checkInWindowToHours?.toString() ?? '',
    nameChanges: policy === null ? 'NotAllowed' : 'Allowed',
    nameChangeDeadlineValue: policy?.deadlineValue?.toString() ?? '',
    nameChangeDeadlineUnit: policy?.deadlineUnit ?? DeadlineUnit.Hours,
    nameChangeFeeMode: policy?.feeMode ?? null,
    nameChangeOnlineFee: policy?.onlineFee?.toString() ?? '',
    nameChangeAirportFee: policy?.airportFee?.toString() ?? '',
    nameChangeNamelistValue: policy?.namelistValue?.toString() ?? '',
    nameChangeNamelistUnit: policy?.namelistUnit ?? DeadlineUnit.Hours,
    nameChangeNote: policy?.note ?? '',
  };
}
